import { cn } from "@design-system/utils";
import { SearchIcon } from "lucide-react";
import React, {
  type ReactNode,
  useEffect,
  useCallback,
  useRef,
  useState,
  ChangeEvent,
} from "react";
import { useDebouncedCallback } from "use-debounce";

import type { InputProps } from "../input";
import {
  InputGroup,
  InputGroupLabelAddon,
  InputGroupInput,
} from "../input-group";

export interface SearchInputProps extends InputProps {
  debounceMs: number;
  children?: ReactNode;
}

export function SearchInput({
  value,
  onChange,
  debounceMs,
  className,
  variant,
  children,
  ...props
}: SearchInputProps) {
  const [inputValue, setInputValue] = useState(value);
  const reportedValue = useRef(value);

  const reportChange = useDebouncedCallback((nextValue: string) => {
    reportedValue.current = nextValue;

    const syntheticEvent = {
      target: { value: nextValue },
      currentTarget: { value: nextValue },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange?.(syntheticEvent);
  }, debounceMs);

  useEffect(() => {
    if (value === reportedValue.current) {
      return;
    }

    reportedValue.current = value;
    reportChange.cancel();
    setInputValue(value);
  }, [value, reportChange]);

  const handleSetInputValue = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      reportChange(e.target.value);
    },
    [reportChange]
  );

  return (
    <InputGroup
      className={cn(
        variant === "pill" && "rounded-full",
        "has-[>[data-align=inline-end]]:[&>input]:pr-1!",
        className
      )}
    >
      <InputGroupLabelAddon align="inline-start" className="pl-2.75">
        <SearchIcon
          className="text-shape-neutral-outlined-foreground-muted"
          aria-hidden
        />
      </InputGroupLabelAddon>
      <InputGroupInput
        {...props}
        value={inputValue}
        onChange={handleSetInputValue}
      />
      {children}
    </InputGroup>
  );
}
